import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import photo1 from "../../Assets/Images/landing/hero-a.jpg";
import photo2 from "../../Assets/Images/landing/hero-b.jpg";
import photo3 from "../../Assets/Images/landing/hero-c.jpg";
import photo4 from "../../Assets/Images/landing/hero-d.jpg";
import photo5 from "../../Assets/Images/landing/hero-a.jpg";
import photo6 from "../../Assets/Images/landing/hero-b.jpg";
import { useTranslation } from "react-i18next";

const photos = [
  { src: photo1, label: "Nordic Peaks" },
  { src: photo2, label: "Neon Alley" },
  { src: photo3, label: "Studio 07" },
  { src: photo4, label: "Coastline" },
  { src: photo5, label: "Morning" },
  { src: photo6, label: "Savanna" },
];

const streamPhotos = [
  { src: photos[0].src, top: "8%", left: "4%", width: "140px", rot: "-8deg", delay: "0s" },
  { src: photos[1].src, top: "18%", left: "82%", width: "160px", rot: "6deg", delay: "1.2s" },
  { src: photos[2].src, top: "52%", left: "2%", width: "130px", rot: "5deg", delay: "2.4s" },
  { src: photos[3].src, top: "62%", left: "88%", width: "150px", rot: "-6deg", delay: "3.6s" },
  { src: photos[4].src, top: "82%", left: "14%", width: "120px", rot: "8deg", delay: "4.8s" },
  { src: photos[5].src, top: "78%", left: "72%", width: "140px", rot: "-4deg", delay: "6s" },
  { src: photos[1].src, top: "36%", left: "-4%", width: "110px", rot: "10deg", delay: "7.2s" },
  { src: photos[3].src, top: "42%", left: "94%", width: "120px", rot: "-10deg", delay: "8.4s" },
];

const sparkles = [
  { top: "12%", left: "8%", delay: "0s" },
  { top: "22%", left: "88%", delay: "0.8s" },
  { top: "48%", left: "4%", delay: "1.6s" },
  { top: "8%", left: "62%", delay: "2.2s" },
  { top: "38%", left: "94%", delay: "1.1s" },
  { top: "58%", left: "48%", delay: "2.6s" },
];

function useMouseParallax() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onMove = (e: MouseEvent) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      el.style.setProperty("--mx", x.toFixed(3));
      el.style.setProperty("--my", y.toFixed(3));
    };
    el.addEventListener("mousemove", onMove);
    return () => el.removeEventListener("mousemove", onMove);
  }, []);
  return ref;
}

interface FloatingPhotoProps {
  src: string;
  label: string;
  className: string;
  rotate: string;
  depth?: number;
  delay?: number;
  featured?: boolean;
}

const FloatingPhoto: React.FC<FloatingPhotoProps> = ({
  src,
  label,
  className,
  rotate,
  depth = 40,
  delay = 0,
  featured = false,
}) => {
  return (
    <div
      className={`lr-floating-photo ${className}`}
      style={{
        animationDelay: `${delay}s`,
        transform: `translate3d(calc(var(--mx, 0) * ${depth}px), calc(var(--my, 0) * ${depth}px), 0)`,
        transition: "transform 0.4s cubic-bezier(0.2, 0.9, 0.3, 1)",
      }}
    >
      <div className="lr-floating-photo__inner" style={{ ["--rotate" as string]: rotate }}>
        <img src={src} alt={label} loading="lazy" />
        <div className="lr-floating-photo__tile">
          <span>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i}>eZy · © eZy WATERMARK · eZy · © eZy ·</div>
            ))}
          </span>
        </div>
        <div className="lr-floating-photo__corner">
          <span className="lr-floating-photo__corner-badge">eZ</span>
          ezywatermark.app
        </div>
        {featured && (
          <>
            <div className="lr-floating-photo__live">● live preview</div>
            <div className="lr-floating-photo__scan" />
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Redesigned landing hero -- animated floating photo stack + drifting background photos.
 * `handleStarted` below is the original, untouched OS-detection redirect logic from the old
 * Banner component (App Store / Play Store / "/home" fallback) -- only the surrounding visuals
 * changed.
 */
const Hero: React.FC = () => {
  const ref = useMouseParallax();
  const history = useHistory();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const { t } = useTranslation();

  const handleStarted = () => {
    const win = window as Window & { opera?: string; MSStream?: unknown };
    const userAgent = navigator.userAgent || navigator.vendor || win.opera;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent as string) && !win.MSStream;
    const isAndroid = /Android/.test(userAgent as string);

    if (isIOS) {
      window.location.href =
        "https://apps.apple.com/us/app/ezy-watermark-photos-lite/id494473910";
    } else if (isAndroid) {
      window.location.href =
        "https://play.google.com/store/apps/details?id=com.whizpool.ezywatermarklite";
    } else {
      history.push("/home");
    }
  };

  return (
    <section ref={ref} className="lr-hero lr-grain" style={{ position: "relative" }}>
      <div className="lr-hero__orbs">
        <div className="lr-hero__orb lr-hero__orb--1" />
        <div className="lr-hero__orb lr-hero__orb--2" />
        <div className="lr-hero__orb lr-hero__orb--3" />
      </div>

      <div className="lr-hero__stream">
        {streamPhotos.map((p, i) => (
          <div
            key={i}
            className="lr-hero__stream-photo"
            style={{
              top: p.top,
              left: p.left,
              width: p.width,
              animationDelay: p.delay,
              ["--rot" as string]: p.rot,
            }}
          >
            <img src={p.src} alt="" loading="lazy" />
          </div>
        ))}
      </div>

      <div className="lr-hero__content">
        <div className="lr-hero__sparkles">
          {sparkles.map((s, i) => (
            <span
              key={i}
              className="lr-sparkle-dot"
              style={{ position: "absolute", top: s.top, left: s.left, animationDelay: s.delay }}
            />
          ))}
        </div>

        <div className="lr-hero__center">
          <span className={`lr-hero__badge ${mounted ? "mounted" : ""}`}>
            <span className="lr-hero__badge-dot" />
            eZy Watermark Photos — free &amp; fast
          </span>
          <h1 className="lr-hero__title">
            <span className="lr-hero-word" style={{ animationDelay: "0.1s" }}>
              <span className="lr-text-gradient">Add Watermarks</span>
            </span>
            <br />
            <span className="lr-hero-word" style={{ animationDelay: "0.35s" }}>
              <span className="lr-text-gradient">on&nbsp;</span>
            </span>
            <span className="lr-hero-word" style={{ animationDelay: "0.55s" }}>
              <span className="lr-text-gradient">Photos.</span>
            </span>
          </h1>
          <p className={`lr-hero__subtitle ${mounted ? "mounted" : ""}`}>
            eZy Watermark Photos Free offers you the optimal solution of capturing and
            watermarking your photos — batch processing, custom templates, and mobile apps.
          </p>
          <div className={`lr-hero__ctas ${mounted ? "mounted" : ""}`}>
            <button className="lr-hero__cta-primary" onClick={handleStarted}>
              {t("watermarkNow")}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
            <a
              href="https://www.youtube.com/watch?v=tQ7SUuh3gjs&source_ve_path=OTY3MTQ&embeds_referring_euri=https%3A%2F%2Fwww.ezywatermark.com%2F"
              target="_blank"
              rel="noopener noreferrer"
              className="lr-hero__cta-secondary"
            >
              Watch demo
            </a>
          </div>
        </div>

        <div className="lr-hero__stack">
          <FloatingPhoto
            src={photos[0].src}
            label={photos[0].label}
            className="lr-floating-photo--1"
            rotate="-8deg"
            depth={40}
            delay={0}
          />
          <FloatingPhoto
            src={photos[1].src}
            label={photos[1].label}
            className="lr-floating-photo--2"
            rotate="7deg"
            depth={70}
            delay={0.3}
          />
          <FloatingPhoto
            src={photos[2].src}
            label={photos[2].label}
            className="lr-floating-photo--3"
            rotate="-4deg"
            depth={90}
            delay={0.6}
          />
          <FloatingPhoto
            src={photos[3].src}
            label={photos[3].label}
            className="lr-floating-photo--4"
            rotate="5deg"
            depth={20}
            delay={0.9}
            featured
          />
        </div>
      </div>
    </section>
  );
};

export default Hero;
